import React, { useState, useEffect } from "react";
import axios from "axios";
import { API } from "../../App";
import { Button } from "../../components/ui/button";
import { Input } from "../../components/ui/input";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../../components/ui/select";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "../../components/ui/table";
import { Card, CardContent } from "../../components/ui/card";
import { toast } from "sonner";
import { Award, DollarSign, Trophy, CheckCircle, Lock, Unlock, Save, Edit2 } from "lucide-react";

const PayoutPointsPage = () => {
  const [playdays, setPlaydays] = useState([]);
  const [selectedPlayday, setSelectedPlayday] = useState("");
  const [draws, setDraws] = useState([]);
  const [loading, setLoading] = useState(true);
  const [applying, setApplying] = useState({});
  const [editMode, setEditMode] = useState({}); // Track which draws are in edit mode
  const [manualValues, setManualValues] = useState({}); // Track manual input values
  const [saving, setSaving] = useState({});

  useEffect(() => {
    fetchPlaydays();
  }, []);

  useEffect(() => {
    if (selectedPlayday) {
      fetchDraws();
    }
  }, [selectedPlayday]);

  const fetchPlaydays = async () => {
    try {
      const response = await axios.get(`${API}/playdays`);
      setPlaydays(response.data);
      if (response.data.length > 0) {
        setSelectedPlayday(response.data[0].id);
      }
    } catch (error) {
      toast.error("Failed to fetch playdays");
    } finally {
      setLoading(false);
    }
  };

  const fetchDraws = async () => {
    try {
      const response = await axios.get(`${API}/draws?playday_id=${selectedPlayday}`);
      setDraws(response.data);
      // Initialize manual values from current data
      const values = {};
      response.data.forEach(draw => {
        values[draw.id] = {};
        draw.entries.forEach(entry => {
          values[draw.id][entry.id] = {
            payout: entry.payout || "",
            points: entry.points || ""
          };
        });
      });
      setManualValues(values);
    } catch (error) {
      toast.error("Failed to fetch draws");
    }
  };

  const applyPayoutsPoints = async (drawId) => {
    setApplying(prev => ({ ...prev, [drawId]: true }));
    try {
      await axios.put(`${API}/draws/${drawId}/apply-payouts-points`);
      toast.success("Payouts and points applied (draw is now locked)");
      setEditMode(prev => ({ ...prev, [drawId]: false }));
      fetchDraws();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to apply payouts and points");
    } finally {
      setApplying(prev => ({ ...prev, [drawId]: false }));
    }
  };

  const unlockDraw = async (drawId) => {
    try {
      await axios.put(`${API}/draws/${drawId}/unlock`);
      toast.success("Draw unlocked for editing");
      fetchDraws();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to unlock draw");
    }
  };

  const toggleEditMode = (drawId) => {
    setEditMode(prev => ({ ...prev, [drawId]: !prev[drawId] }));
  };

  const handleManualValueChange = (drawId, entryId, field, value) => {
    setManualValues(prev => ({
      ...prev,
      [drawId]: {
        ...prev[drawId],
        [entryId]: {
          ...prev[drawId]?.[entryId],
          [field]: value
        }
      }
    }));
  };

  const saveManualValues = async (drawId) => {
    setSaving(prev => ({ ...prev, [drawId]: true }));
    try {
      const draw = draws.find(d => d.id === drawId);
      const updates = draw.entries
        .filter(e => e.total !== null)
        .map(entry => ({
          draw_entry_id: entry.id,
          payout: manualValues[drawId]?.[entry.id]?.payout !== undefined && manualValues[drawId][entry.id].payout !== ""
            ? parseFloat(manualValues[drawId][entry.id].payout) 
            : null,
          points: manualValues[drawId]?.[entry.id]?.points !== undefined && manualValues[drawId][entry.id].points !== ""
            ? parseInt(manualValues[drawId][entry.id].points) 
            : null
        }));

      await axios.put(`${API}/draws/${drawId}/manual-payout-points`, updates);
      toast.success("Manual values saved — click Finalize to lock");
      setEditMode(prev => ({ ...prev, [drawId]: false }));
      fetchDraws();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to save manual values");
    } finally {
      setSaving(prev => ({ ...prev, [drawId]: false }));
    }
  };

  const finalizeDrawPayouts = async (drawId) => {
    setApplying(prev => ({ ...prev, [drawId]: true }));
    try {
      await axios.put(`${API}/draws/${drawId}/finalize-payouts`);
      toast.success("Payouts finalized and locked");
      fetchDraws();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to finalize payouts");
    } finally {
      setApplying(prev => ({ ...prev, [drawId]: false }));
    }
  };

  const applyAllPayouts = async () => {
    for (const draw of draws) {
      if (!draw.is_locked) {
        await applyPayoutsPoints(draw.id);
      }
    }
  };

  const isPlaydayFinalized = () => {
    const playday = playdays.find(p => p.id === selectedPlayday);
    return playday?.is_finalized || false;
  };

  const isRopingEvent = (eventName) => {
    return eventName.includes("Team Roping") || eventName.includes("Ribbon Roping");
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-12 w-12 border-4 border-primary border-t-transparent"></div>
      </div>
    );
  }

  // Payout table info - now uses contestant count based amounts
  const payoutInfo = "Payouts are based on number of contestants. Example: 10 contestants → 1st=$8, 2nd=$6, 3rd=$4, 4th=$2";

  const pointsInfo = [
    { place: "1st", points: 10 }, { place: "2nd", points: 9 }, { place: "3rd", points: 8 },
    { place: "4th", points: 7 }, { place: "5th", points: 6 }, { place: "6th", points: 5 },
    { place: "7th", points: 4 }, { place: "8th", points: 3 }, { place: "9th", points: 2 },
    { place: "10th", points: 1 }
  ];

  return (
    <div className="space-y-6" data-testid="payout-points-page">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="font-heading font-bold text-3xl text-text-primary">Payout & Points</h1>
          <p className="text-text-secondary mt-1">Apply or manually enter payouts and points</p>
        </div>
      </div>

      {/* Reference Tables */}
      <div className="grid md:grid-cols-2 gap-4">
        <Card className="border-primary/20">
          <CardContent className="pt-6">
            <div className="flex items-center gap-2 mb-4">
              <DollarSign className="h-5 w-5 text-status-success" />
              <h3 className="font-heading font-bold text-text-primary">Payout Table</h3>
            </div>
            <p className="text-sm text-text-secondary">{payoutInfo}</p>
            <p className="text-xs text-text-muted mt-2">Ties split combined payout equally. Use "Manual Entry" to override.</p>
          </CardContent>
        </Card>
        <Card className="border-primary/20">
          <CardContent className="pt-6">
            <div className="flex items-center gap-2 mb-4">
              <Trophy className="h-5 w-5 text-accent" />
              <h3 className="font-heading font-bold text-text-primary">Points Table (Members Only)</h3>
            </div>
            <div className="grid grid-cols-5 gap-2">
              {pointsInfo.map((p) => (
                <div key={p.place} className="text-center p-2 bg-secondary/50 rounded">
                  <p className="text-xs text-text-muted">{p.place}</p>
                  <p className="font-bold text-text-primary">{p.points}</p>
                </div>
              ))}
            </div>
            <p className="text-xs text-text-muted mt-2">Use "Manual Entry" to give points to non-members.</p>
          </CardContent>
        </Card>
      </div>

      {/* Controls */}
      <div className="flex gap-4 items-end flex-wrap">
        <div className="w-64">
          <label className="text-sm font-medium text-text-primary mb-2 block">Select Play Day</label>
          <Select value={selectedPlayday} onValueChange={setSelectedPlayday}>
            <SelectTrigger data-testid="playday-select">
              <SelectValue placeholder="Select a play day" />
            </SelectTrigger>
            <SelectContent>
              {playdays.map((p) => (
                <SelectItem key={p.id} value={p.id}>
                  {p.name} ({p.date}) {p.is_finalized && "✓"}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>
        <Button 
          onClick={applyAllPayouts}
          className="bg-accent text-white hover:bg-accent/90"
          disabled={isPlaydayFinalized()}
          data-testid="apply-all-btn"
        >
          <Award className="h-4 w-4 mr-2" />
          Auto Apply All
        </Button>
      </div>

      {/* Finalized Warning */}
      {isPlaydayFinalized() && (
        <div className="bg-green-50 border border-green-200 rounded-lg p-4 flex items-center gap-2">
          <Lock className="h-5 w-5 text-green-600" />
          <span className="text-green-800 font-medium">This playday is finalized. All data is protected.</span>
        </div>
      )}

      {/* Events List */}
      <div className="space-y-4">
        {draws.length === 0 ? (
          <div className="bg-white rounded-xl border border-primary/20 p-12 text-center">
            <Award className="h-12 w-12 text-text-muted mx-auto mb-4" />
            <p className="text-text-secondary">No draws available. Generate draws first.</p>
          </div>
        ) : (
          draws.map((draw) => {
            const isRoping = isRopingEvent(draw.event_name);
            const isEditing = editMode[draw.id];
            const entriesWithTimes = draw.entries
              .filter(e => e.total !== null)
              .sort((a, b) => a.total - b.total);
            
            return (
              <div key={draw.id} className="bg-white rounded-xl border border-primary/20 overflow-hidden">
                <div className="p-4 border-b border-primary/20 flex items-center justify-between flex-wrap gap-2">
                  <div className="flex items-center gap-3">
                    <h3 className="font-heading font-bold text-lg text-text-primary">{draw.event_name}</h3>
                    {draw.is_locked && (
                      <span className="inline-flex items-center gap-1 px-2 py-1 bg-yellow-100 text-yellow-700 rounded text-xs font-medium">
                        <Lock className="h-3 w-3" />
                        Locked
                      </span>
                    )}
                    {draw.is_finalized && (
                      <span className="inline-flex items-center gap-1 px-2 py-1 bg-status-success/20 text-status-success rounded text-xs font-medium">
                        <CheckCircle className="h-3 w-3" />
                        Applied
                      </span>
                    )}
                  </div>
                  <div className="flex gap-2">
                    {draw.is_locked && !isPlaydayFinalized() && (
                      <Button
                        variant="outline"
                        size="sm"
                        onClick={() => unlockDraw(draw.id)}
                        data-testid={`unlock-${draw.id}`}
                      >
                        <Unlock className="h-4 w-4 mr-2" />
                        Unlock
                      </Button>
                    )}
                    {!draw.is_locked && !isPlaydayFinalized() && (
                      <>
                        {isEditing ? (
                          <Button
                            variant="default"
                            size="sm"
                            onClick={() => saveManualValues(draw.id)}
                            disabled={saving[draw.id]}
                            className="bg-status-success text-white hover:bg-status-success/90"
                            data-testid={`save-manual-${draw.id}`}
                          >
                            <Save className="h-4 w-4 mr-2" />
                            {saving[draw.id] ? "Saving..." : "Save Manual"}
                          </Button>
                        ) : (
                          <Button
                            variant="outline"
                            size="sm"
                            onClick={() => toggleEditMode(draw.id)}
                            data-testid={`manual-entry-${draw.id}`}
                          >
                            <Edit2 className="h-4 w-4 mr-2" />
                            Manual Entry
                          </Button>
                        )}
                        <Button
                          variant={draw.is_finalized ? "outline" : "default"}
                          size="sm"
                          onClick={() => applyPayoutsPoints(draw.id)}
                          disabled={applying[draw.id]}
                          className={draw.is_finalized ? "" : "bg-primary text-primary-foreground hover:bg-primary-hover"}
                          data-testid={`apply-${draw.id}`}
                        >
                          {applying[draw.id] ? "Applying..." : "Auto Apply"}
                        </Button>
                        {!isEditing && (
                          <Button
                            size="sm"
                            onClick={() => finalizeDrawPayouts(draw.id)}
                            disabled={applying[draw.id]}
                            className="bg-accent text-white hover:bg-accent/90"
                            data-testid={`finalize-${draw.id}`}
                          >
                            <CheckCircle className="h-4 w-4 mr-2" />
                            Finalize
                          </Button>
                        )}
                      </>
                    )}
                  </div>
                </div>
                <Table>
                  <TableHeader>
                    <TableRow className="bg-secondary/50">
                      <TableHead className="font-heading font-bold text-text-primary w-16">Place</TableHead>
                      <TableHead className="font-heading font-bold text-text-primary">Contestant 1</TableHead>
                      {isRoping && (
                        <TableHead className="font-heading font-bold text-text-primary">Contestant 2</TableHead>
                      )}
                      <TableHead className="font-heading font-bold text-text-primary text-right w-28">Total Time</TableHead>
                      <TableHead className="font-heading font-bold text-text-primary text-right w-32">
                        Payout ($)
                      </TableHead>
                      <TableHead className="font-heading font-bold text-text-primary text-right w-24">
                        Points
                      </TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {entriesWithTimes.length === 0 ? (
                      <TableRow>
                        <TableCell colSpan={isRoping ? 6 : 5} className="text-center py-4 text-text-secondary">
                          No times recorded yet
                        </TableCell>
                      </TableRow>
                    ) : (
                      entriesWithTimes.map((entry, idx) => (
                        <TableRow key={entry.id} className={`hover:bg-surface-muted ${entry.total > 400 ? "opacity-50 bg-gray-50" : ""}`}>
                          <TableCell className="font-heading font-bold text-primary">{idx + 1}</TableCell>
                          <TableCell className="font-medium text-text-primary">{entry.contestant_name}</TableCell>
                          {isRoping && (
                            <TableCell className="text-text-secondary">{entry.contestant2_name || "-"}</TableCell>
                          )}
                          <TableCell className="text-right font-mono">
                            {entry.total?.toFixed(3)}s
                            {entry.total > 400 && (
                              <span className="ml-1 text-xs text-red-500 font-sans">(NT)</span>
                            )}
                          </TableCell>
                          <TableCell className="text-right">
                            {isEditing ? (
                              <Input
                                type="number"
                                step="0.01"
                                placeholder="0.00"
                                value={manualValues[draw.id]?.[entry.id]?.payout || ""}
                                onChange={(e) => handleManualValueChange(draw.id, entry.id, "payout", e.target.value)}
                                className="w-24 h-8 text-right font-mono"
                                data-testid={`payout-input-${entry.id}`}
                              />
                            ) : (
                              <span className="font-mono font-bold text-status-success">
                                {entry.payout ? `$${entry.payout.toFixed(2)}` : "-"}
                              </span>
                            )}
                          </TableCell>
                          <TableCell className="text-right">
                            {isEditing ? (
                              <Input
                                type="number"
                                step="1"
                                min="0"
                                max="10"
                                placeholder="0"
                                value={manualValues[draw.id]?.[entry.id]?.points || ""}
                                onChange={(e) => handleManualValueChange(draw.id, entry.id, "points", e.target.value)}
                                className="w-20 h-8 text-right font-mono"
                                data-testid={`points-input-${entry.id}`}
                              />
                            ) : (
                              <span className="font-bold text-accent">
                                {entry.points || "-"}
                              </span>
                            )}
                          </TableCell>
                        </TableRow>
                      ))
                    )}
                  </TableBody>
                </Table>
              </div>
            );
          })
        )}
      </div>
    </div>
  );
};

export default PayoutPointsPage;
